import { MDBBtn } from "mdb-react-ui-kit";
import { Dispatch, FC, SetStateAction, useMemo } from "react";
import { TourOption } from "typings/mdb-react-ui-kit";
import mediaControlSvg from "../../utils/svg-icon/mediaControl";
import styles from "../../styles/search/Search.Search.module.scss";
import { randomUid } from "../../utils/randomIds";
import { ETourType } from "../../models/common/enums";

const TourDestinationsFiltersRecap: FC<{
    minPrice: number;
    maxPrice: number;
    selectedDestinations: string[];
    selectedActivities: string[];
    selectedPrice: number;
    selectedMinPrice?: number;
    selectedTourType: string[];
    tourOption: TourOption;
    setSelectedDestinations: Dispatch<SetStateAction<string[]>>;
    setSelectedActivities: Dispatch<SetStateAction<string[]>>;
    setSelectedPrice: Dispatch<SetStateAction<number>>;
    setSelectedMinPrice?: Dispatch<SetStateAction<number>>;
    setSelectedTourType: Dispatch<SetStateAction<string[]>>;
    setTourOption: Dispatch<SetStateAction<TourOption>>;
    selectedTagDestination?: string | null;
    setSelectedTagDestination?: Dispatch<SetStateAction<string | null>>;
    tagDestinations?: Array<{ tagDestination: string; title: string }>;
}> = ({
    minPrice,
    maxPrice,
    selectedDestinations,
    selectedActivities,
    selectedPrice,
    selectedMinPrice,
    selectedTourType,
    tourOption: tourType,
    setSelectedDestinations,
    setSelectedActivities,
    setSelectedPrice,
    setSelectedMinPrice,
    setSelectedTourType,
    setTourOption: setTourType,
    selectedTagDestination,
    setSelectedTagDestination,
    tagDestinations,
}) => {
    const handleCheckboxChange = (
        value: string,
        setValue: Dispatch<SetStateAction<string[]>>
    ) => {
        setValue(prevState => {
            const index = prevState.indexOf(value);
            if (index === -1) {
                return [...prevState, value];
            }
            return prevState.filter(item => item !== value);
        });
    };

    const getTagDestinationTitle = useMemo(() => {
        return (tagDestinationValue: string) => {
            if (!tagDestinationValue) return "";

            // Normalizza il valore: trasforma "-and-" in "--" per matchare il database
            // Gestisce anche il caso in cui il valore è già decodificato dall'URL
            let normalizedValue = tagDestinationValue;
            if (normalizedValue.includes("-and-")) {
                normalizedValue = normalizedValue.replace(/-and-/g, "--");
            }

            // Cerca il title corrispondente nel tagDestinations
            if (tagDestinations && tagDestinations.length > 0) {
                const matchingTag = tagDestinations.find(
                    tag =>
                        tag.tagDestination === normalizedValue ||
                        tag.tagDestination === tagDestinationValue ||
                        tag.tagDestination ===
                            tagDestinationValue.replace(/-and-/g, "--")
                );
                if (matchingTag?.title) {
                    // Sostituisce "&" con "and" nel title per sicurezza
                    return matchingTag.title.replace(/&/g, " and ");
                }
            }
            // Fallback: trasforma il tagDestination in un label leggibile
            // Trasforma "--" o "-and-" in " and " per mostrare "and" invece di "&" nel label
            let transformed = normalizedValue;
            transformed = transformed.replace(/-and-/g, " and ");
            transformed = transformed.replace(/--/g, " and ");
            transformed = transformed.replace(/&/g, " and ");

            // Se dopo le sostituzioni abbiamo " and " nella stringa, dividiamo per " and " invece che per "-"
            if (transformed.includes(" and ")) {
                return transformed
                    .split(" and ")
                    .map(el => {
                        const trimmed = el.trim();
                        // Rimuove eventuali "-" rimasti e capitalizza
                        const cleaned = trimmed.replace(/-/g, " ");
                        return cleaned
                            .split(" ")
                            .filter(word => word.trim() !== "")
                            .map(
                                word =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1).toLowerCase()
                            )
                            .join(" ");
                    })
                    .filter(el => el.trim() !== "")
                    .join(" and ");
            }

            // Altrimenti dividi per "-" normalmente
            return transformed
                .split("-")
                .filter(el => el.trim() !== "") // Rimuove elementi vuoti
                .map(el => {
                    const trimmed = el.trim();
                    return (
                        trimmed.charAt(0).toUpperCase() +
                        trimmed.slice(1).toLowerCase()
                    );
                })
                .join(" ");
        };
    }, [tagDestinations]);

    return (
        <div className="d-flex flex-wrap w-100">
            {selectedTourType.map((el: string) => (
                <MDBBtn className={styles.filterRecapButton} key={randomUid()}>
                    <span className="ps-2">
                        {ETourType[el as keyof typeof ETourType]}
                    </span>
                    <div
                        className="ps-2 pt-2"
                        onClick={() =>
                            handleCheckboxChange(el, setSelectedTourType)
                        }
                    >
                        <mediaControlSvg.CloseCircleFilled />
                    </div>
                </MDBBtn>
            ))}
            {selectedDestinations.map((el: string) => (
                <MDBBtn className={styles.filterRecapButton} key={randomUid()}>
                    <span className="ps-2">{el}</span>
                    <div
                        className="ps-2 pt-2"
                        onClick={() =>
                            handleCheckboxChange(el, setSelectedDestinations)
                        }
                    >
                        <mediaControlSvg.CloseCircleFilled />
                    </div>
                </MDBBtn>
            ))}
            {selectedActivities.map((el: string) => (
                <MDBBtn className={styles.filterRecapButton} key={randomUid()}>
                    <span className="ps-2">{el}</span>
                    <div
                        className="ps-2 pt-2"
                        onClick={() =>
                            handleCheckboxChange(el, setSelectedActivities)
                        }
                    >
                        <mediaControlSvg.CloseCircleFilled />
                    </div>
                </MDBBtn>
            ))}
            {selectedTagDestination && (
                <MDBBtn className={styles.filterRecapButton}>
                    <span className="ps-2">
                        {getTagDestinationTitle(selectedTagDestination)}
                    </span>
                    <div
                        className="ps-2 pt-2"
                        onClick={() => setSelectedTagDestination?.(null)}
                    >
                        <mediaControlSvg.CloseCircleFilled />
                    </div>
                </MDBBtn>
            )}
            {tourType && (
                <MDBBtn className={styles.filterRecapButton}>
                    <span className="ps-2">
                        {tourType.charAt(0).toUpperCase() +
                            tourType.slice(1).toLowerCase()}
                    </span>
                    <div
                        className="ps-2 pt-2"
                        onClick={() => setTourType(undefined)}
                    >
                        <mediaControlSvg.CloseCircleFilled />
                    </div>
                </MDBBtn>
            )}
            {((selectedMinPrice !== undefined && selectedMinPrice > minPrice) ||
                selectedPrice < maxPrice) && (
                <MDBBtn className={styles.filterRecapButton}>
                    <span className="ps-2">
                        {`${selectedMinPrice ?? minPrice} - ${selectedPrice}`}
                    </span>
                    <div
                        className="ps-2 pt-2"
                        onClick={() => {
                            setSelectedPrice(maxPrice);
                            setSelectedMinPrice?.(minPrice);
                        }}
                    >
                        <mediaControlSvg.CloseCircleFilled />
                    </div>
                </MDBBtn>
            )}
        </div>
    );
};

export default TourDestinationsFiltersRecap;
